/**
 * Small presentational pieces shared by the record detail pages (customer,
 * property): money formatting, stat cards and badges.
 */

import React from 'react';

/** "$12.34" for a decimal string from the API (the value itself when it is not a number). */
export function formatMoney(value: string): string {
  const n = Number(value);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : value;
}

interface StatCardProps {
  label: string;
  value: string | number;
  accent?: boolean;
}

export function StatCard({ label, value, accent }: StatCardProps) {
  return (
    <div className="min-w-0 bg-white rounded-xl shadow-sm border border-gray-200 p-3 sm:p-4">
      <p className="text-gray-500 text-xs font-medium uppercase tracking-wide mb-1">{label}</p>
      <p className={`text-xl sm:text-2xl font-bold break-words ${accent ? 'text-red-600' : 'text-gray-900'}`}>{value}</p>
    </div>
  );
}

interface BadgeProps {
  tone: 'blue' | 'green' | 'gray';
  children: React.ReactNode;
}

const BADGE_TONES: Record<BadgeProps['tone'], string> = {
  blue: 'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200',
  green: 'bg-green-50 text-green-700 ring-1 ring-inset ring-green-200',
  gray: 'bg-gray-100 text-gray-700 ring-1 ring-inset ring-gray-200',
};

export function Badge({ tone, children }: BadgeProps) {
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${BADGE_TONES[tone]}`}>
      {children}
    </span>
  );
}
